const ACTIVE_BOOKING_ID_KEY = "activeBookingId";
const ACTIVE_BOOKING_SUMMARY_KEY = "activeBookingSummary";
const BOOKING_FLOW_CACHE_KEY = "bookingFlowCache";
const BOOKING_RETURN_NOTICE_KEY = "bookingReturnNotice";
const BOOKING_STRIPE_RETURN_GUARD_KEY = "bookingStripeReturnGuard";

const readJsonValue = (storageKey) => {
  try {
    const rawValue = localStorage.getItem(storageKey);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (_error) {
    return null;
  }
};

const writeJsonValue = (storageKey, value) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (_error) {
    // Ignore storage failures. The booking flow can continue without persistence.
  }
};

const removeValue = (storageKey) => {
  try {
    localStorage.removeItem(storageKey);
  } catch (_error) {
    // Ignore storage failures.
  }
};

export const readBookingFlowCache = () => {
  const cache = readJsonValue(BOOKING_FLOW_CACHE_KEY);
  return cache && typeof cache === "object" ? cache : {};
};

export const readStoredBookingSummary = (bookingId = "") => {
  const normalizedBookingId = String(bookingId || "").trim();
  const bookingFlowCache = readBookingFlowCache();

  if (
    normalizedBookingId &&
    bookingFlowCache[normalizedBookingId] &&
    typeof bookingFlowCache[normalizedBookingId] === "object"
  ) {
    return bookingFlowCache[normalizedBookingId];
  }

  const activeSummary = readJsonValue(ACTIVE_BOOKING_SUMMARY_KEY);
  if (!activeSummary || typeof activeSummary !== "object") {
    return null;
  }

  if (
    normalizedBookingId &&
    String(activeSummary.bookingId || "").trim() &&
    String(activeSummary.bookingId || "").trim() !== normalizedBookingId
  ) {
    return null;
  }

  return activeSummary;
};

export const persistBookingSummary = (summary = {}) => {
  if (!summary || typeof summary !== "object") {
    return;
  }

  const bookingId = String(summary.bookingId || "").trim();
  const nextSummary = {
    ...summary,
    bookingId,
    updatedAt: Date.now(),
  };

  if (bookingId) {
    writeJsonValue(ACTIVE_BOOKING_ID_KEY, bookingId);
    writeJsonValue(ACTIVE_BOOKING_SUMMARY_KEY, nextSummary);

    const bookingFlowCache = readBookingFlowCache();
    writeJsonValue(BOOKING_FLOW_CACHE_KEY, {
      ...bookingFlowCache,
      [bookingId]: nextSummary,
    });
    return;
  }

  writeJsonValue(ACTIVE_BOOKING_SUMMARY_KEY, nextSummary);
};

export const clearActiveBookingSummary = () => {
  removeValue(ACTIVE_BOOKING_ID_KEY);
  removeValue(ACTIVE_BOOKING_SUMMARY_KEY);
};

export const readStoredBookingReturnNotice = () => {
  const notice = readJsonValue(BOOKING_RETURN_NOTICE_KEY);
  return notice && typeof notice === "object" ? notice : null;
};

export const persistBookingReturnNotice = (notice = {}) => {
  if (!notice || typeof notice !== "object") {
    return;
  }

  writeJsonValue(BOOKING_RETURN_NOTICE_KEY, {
    ...notice,
    updatedAt: Date.now(),
  });
};

export const clearStoredBookingReturnNotice = () => {
  removeValue(BOOKING_RETURN_NOTICE_KEY);
};

export const readStoredBookingStripeReturnGuard = () => {
  const guard = readJsonValue(BOOKING_STRIPE_RETURN_GUARD_KEY);
  return guard && typeof guard === "object" ? guard : null;
};

export const persistBookingStripeReturnGuard = (guard = {}) => {
  if (!guard || typeof guard !== "object") {
    return;
  }

  writeJsonValue(BOOKING_STRIPE_RETURN_GUARD_KEY, {
    ...guard,
    updatedAt: Date.now(),
  });
};

export const clearStoredBookingStripeReturnGuard = () => {
  removeValue(BOOKING_STRIPE_RETURN_GUARD_KEY);
};
